import React from "react";
import Header from "../../components/Customer/Header";
import VehicleGrid from "../../components/Customer/VehicleGrid";
import Footer from "../../components/Customer/Footer";
import { VEHICLES } from "../../constants/vehicles";

function VehiclesPage() {
  const vehicles = VEHICLES;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-amber-100 font-sans">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center mb-6">
            <img 
              src="/images/araliya-tours-logo.png" 
              alt="Araliya Tours" 
              className="h-40 w-auto drop-shadow-lg filter" 
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 font-poppins mb-6">
            Our Vehicle Fleet
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our carefully maintained fleet of vehicles, each selected to provide 
            the perfect balance of comfort, reliability, and style for your journey. 
            From compact sedans to spacious vans, we have the right vehicle for every occasion.
          </p>
        </div>

        {/* Vehicle Categories Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center p-6 bg-white/60 backdrop-blur rounded-2xl border border-white/40">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Sedans</h3>
            <p className="text-gray-600">
              Perfect for city driving and business trips. Comfortable, fuel-efficient, 
              and easy to maneuver in urban environments.
            </p>
          </div>

          <div className="text-center p-6 bg-white/60 backdrop-blur rounded-2xl border border-white/40">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">SUVs</h3>
            <p className="text-gray-600">
              Versatile vehicles ideal for family trips and outdoor adventures. 
              Spacious interiors with excellent ground clearance for various terrains.
            </p>
          </div>

          <div className="text-center p-6 bg-white/60 backdrop-blur rounded-2xl border border-white/40">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Vans</h3>
            <p className="text-gray-600">
              Ideal for group travel and transporting equipment. Generous cargo space 
              and comfortable seating for larger groups or family outings.
            </p>
          </div>
        </div>

        {/* Why Choose Our Vehicles */}
        <div className="bg-white/70 backdrop-blur border border-white/40 shadow-xl ring-1 ring-black/5 rounded-3xl p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Why Choose Our Vehicles?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Well Maintained</h4>
              <p className="text-sm text-gray-600">Regular servicing and safety checks</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Fully Insured</h4>
              <p className="text-sm text-gray-600">Comprehensive coverage for peace of mind</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Fuel Efficient</h4>
              <p className="text-sm text-gray-600">Modern engines for better economy</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">24/7 Support</h4>
              <p className="text-sm text-gray-600">Round-the-clock roadside assistance available</p>
            </div>
          </div>
        </div>

        {/* Vehicles Grid */}
        <section className="rounded-3xl bg-white/70 backdrop-blur border border-white/40 shadow-xl ring-1 ring-black/5">
          <div className="px-6 py-8 sm:px-10 sm:py-12">
            <div className="mb-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-poppins mb-4">
                Available Vehicles
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Browse our selection of vehicles below. Each one is carefully maintained 
                and ready to provide you with a comfortable and reliable journey.
              </p>
            </div>
            <VehicleGrid vehicles={vehicles} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default VehiclesPage;


